import { RefObject, useEffect } from 'react';
import type { ReviewCellRef, ReviewItem } from 'store/useReviewStore';
import { useReviewStore } from 'store/useReviewStore';
import { reviewCellSelector } from '../../utilities/reviewCellSelector';

const HOVERED_CLASS = 'cell-review-hovered';
const SELECTED_CLASS = 'cell-review-selected';

// The purple review ring is for PENDING items only: a ref whose matching review
// item is settled (accepted/dismissed) resolves to null - no outline, no scroll.
// A ref with NO matching item still outlines (doc-jump before aggregation).
// Subscribing through reviewItems means the ring clears on EVERY settle path -
// panel, in-grid popover rebuild, bulk - and can never be re-painted for settled
// items. Passing refs return the STORED object, so zustand's strict-equality
// check skips re-renders on unrelated item updates.
const unsettledRef = (ref: ReviewCellRef | null, items: ReviewItem[]): ReviewCellRef | null => {
  if (!ref) {
    return null;
  }
  const item = items.find(i => i.id === `${ref.securityId}:${ref.fieldAlias}`);
  return item && item.status !== 'pending' ? null : ref;
};

// Strip `className` off every prior cell, then stamp it onto the cell(s) matching `ref`,
// returning the first match (the scroll target). Shared by both outline effects below.
const toggleCellClass = (doc: Document, ref: ReviewCellRef | null, className: string): Element | null => {
  doc.querySelectorAll(`.${className}`).forEach(el => el.classList.remove(className));
  if (!ref) {
    return null;
  }
  const matches = doc.querySelectorAll(reviewCellSelector(ref));
  matches.forEach(el => el.classList.add(className));
  return matches[0] ?? null;
};

interface ReviewCellOutlineProps {
  /**
   * Ref to an element inside the cap-table grid. Its ownerDocument is where the
   * grid cells live; the outline classes are toggled there. The grid always stays
   * in the MAIN window even when the review panel is popped out into another
   * window, so we deliberately resolve the document from the GRID, never the card.
   */
  gridRef?: RefObject<HTMLElement | null>;
}

/**
 * SCAL-8976: headless effect that mirrors `hoveredReviewCell` / `selectedReviewCell`
 * onto grid cell DOM nodes via class toggles - no grid re-render. Mounted once next
 * to the cap-table grid. Hovering a review card outlines its cell (`cell-review-hovered`);
 * the single active card keeps its cell outlined (`cell-review-selected`) AND smooth-scrolls
 * that cell into view (Feature A). The `.cell-review-selected` rule carries a
 * scroll-margin-bottom of the bottom-drawer height (see _cell.scss) so the cell parks
 * above the fixed drawer instead of behind it.
 */
const ReviewCellOutline = ({ gridRef }: ReviewCellOutlineProps) => {
  const hoveredReviewCell = useReviewStore(s => unsettledRef(s.hoveredReviewCell, s.reviewItems));
  const selectedReviewCell = useReviewStore(s => unsettledRef(s.selectedReviewCell, s.reviewItems));

  // HOVER: outline only, never scroll. Isolated in its own effect (deps = hover) so a fast
  // hover sweep across the card list cannot re-run the SELECTED scroll path below.
  useEffect(() => {
    const doc = gridRef?.current?.ownerDocument ?? document;
    toggleCellClass(doc, hoveredReviewCell, HOVERED_CLASS);
    return () => {
      doc.querySelectorAll(`.${HOVERED_CLASS}`).forEach(el => el.classList.remove(HOVERED_CLASS));
    };
  }, [hoveredReviewCell, gridRef]);

  // SELECTED: outline AND smooth-scroll into view. Depends ONLY on the selection (not hover),
  // so moving the hovered cell never re-fires this scroll - a manual scroll after selecting
  // stays put. rAF lets the outline class paint first; `nearest` no-ops when the cell is
  // already fully visible, and the cell's scroll-margin-bottom (set by .cell-review-selected
  // in _cell.scss) keeps it above the drawer. The pending frame is cancelled on cleanup so a
  // rapid re-selection cannot land a stale scroll on the previous cell.
  useEffect(() => {
    const doc = gridRef?.current?.ownerDocument ?? document;
    const selectedEl = toggleCellClass(doc, selectedReviewCell, SELECTED_CLASS);
    const rafId = selectedEl
      ? requestAnimationFrame(() => {
        selectedEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      })
      : 0;
    return () => {
      cancelAnimationFrame(rafId);
      doc.querySelectorAll(`.${SELECTED_CLASS}`).forEach(el => el.classList.remove(SELECTED_CLASS));
    };
  }, [selectedReviewCell, gridRef]);

  return null;
};

export default ReviewCellOutline;
